// src/pages/Home.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Box, Button, Grid, Typography, Stack, Paper, Chip, alpha } from "@mui/material";
import TimerIcon       from "@mui/icons-material/Timer";
import QuizIcon        from "@mui/icons-material/Quiz";
import UploadFileIcon  from "@mui/icons-material/UploadFile";
import BarChartIcon    from "@mui/icons-material/BarChart";
import SecurityIcon    from "@mui/icons-material/Security";
import ShuffleIcon     from "@mui/icons-material/Shuffle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { C, heroGradient, gradientText } from "../theme";
import { isAuthenticated } from "../auth";

const FEATURE_KEYS = [
  { icon: <TimerIcon />,      color: C.indigo[500], key: "timer" },
  { icon: <QuizIcon />,       color: C.teal[600],   key: "qcm" },
  { icon: <ShuffleIcon />,    color: C.amber[600],  key: "antiCheat" },
  { icon: <UploadFileIcon />, color: "#e11d48",     key: "import" },
  { icon: <BarChartIcon />,   color: "#7c3aed",     key: "stats" },
  { icon: <SecurityIcon />,   color: C.teal[600],   key: "security" },
];

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authed = isAuthenticated();

  return (
    <Box>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box sx={{
        position: "relative", borderRadius: "20px", overflow: "hidden",
        background: heroGradient,
        px: { xs: 4, sm: 6, md: 10 }, py: { xs: 8, md: 13 }, mb: 6,
      }}>
        {/* Mesh overlay */}
        <Box sx={{ position:"absolute", inset:0, opacity:.08, backgroundImage:`linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)`, backgroundSize:"40px 40px", pointerEvents:"none" }} />
        <Box sx={{ position:"absolute", top:-120, right:-80, width:460, height:460, borderRadius:"50%", background:`radial-gradient(circle, ${alpha(C.teal[400],.25)} 0%, transparent 65%)`, pointerEvents:"none" }} />
        <Box sx={{ position:"absolute", bottom:-80, left:"30%", width:320, height:320, borderRadius:"50%", background:`radial-gradient(circle, ${alpha(C.indigo[300],.2)} 0%, transparent 65%)`, pointerEvents:"none" }} />

        <Box sx={{ position: "relative", maxWidth: 700 }}>
          <Chip label={t("home.badge")} size="small" className="au"
            sx={{ mb: 3, background: alpha("#fff",.15), color: "rgba(255,255,255,.9)", fontWeight: 600, backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,.2)" }} />
          <Typography variant="h2" className="au au1" sx={{ color: "white", lineHeight: 1.12, mb: 2.5 }}>
            {t("home.heroTitle1")}{" "}
            <Box component="span" sx={{ color: alpha(C.teal[300], 1) }}>{t("home.heroTitle2")}</Box>
          </Typography>
          <Typography className="au au2" sx={{ color: "rgba(255,255,255,.72)", fontSize: "1.1rem", lineHeight: 1.8, mb: 5, maxWidth: 520 }}>
            {t("home.heroDesc")}
          </Typography>
          <Stack direction="row" spacing={2} className="au au3" flexWrap="wrap">
            <Button size="large" variant="contained" endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(authed ? "/exams" : "/register")}
              sx={{ px: 3.5, py: 1.5, background: "white", color: C.indigo[700], "&:hover": { background: C.indigo[50], transform: "translateY(-1px)", boxShadow: "0 6px 20px rgba(255,255,255,.25)" } }}>
              {authed ? t("home.viewExams") : t("home.startFree")}
            </Button>
            {!authed && (
              <Button size="large" variant="outlined" onClick={() => navigate("/login")}
                sx={{ px: 3, py: 1.5, borderColor: "rgba(255,255,255,.35)", color: "white", "&:hover": { borderColor: "white", background: "rgba(255,255,255,.08)" } }}>
                {t("home.login")}
              </Button>
            )}
          </Stack>

          {/* Stats */}
          <Stack direction="row" spacing={4} sx={{ mt: 7 }} className="au au4" flexWrap="wrap" gap={2}>
            {[
              { v: "100%",    l: t("home.statSecure") },
              { v: "3 types", l: t("home.statTypes") },
              { v: "< 1 s",   l: t("home.statCorrection") },
            ].map(s => (
              <Box key={s.l}>
                <Typography sx={{ fontWeight: 800, fontSize: "1.6rem", color: "white", lineHeight: 1 }}>{s.v}</Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.55)", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase" }}>{s.l}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Typography variant="overline" color="text.secondary">{t("home.featuresLabel")}</Typography>
        <Typography variant="h3" sx={{ mt: 0.5 }}>
          {t("home.featuresTitle1")}{" "}
          <Box component="span" sx={gradientText}>{t("home.featuresTitle2")}</Box>
        </Typography>
      </Box>
      <Grid container spacing={2.5} sx={{ mb: 7 }}>
        {FEATURE_KEYS.map((f, i) => (
          <Grid item xs={12} sm={6} md={4} key={f.key} className={`au au${Math.min(i%4+1,4)}`}>
            <Paper sx={{ p: 3, height: "100%", "&:hover": { borderColor: f.color } }}>
              <Box sx={{ width:44, height:44, borderRadius:2.5, mb:2, background:`${f.color}14`, display:"flex", alignItems:"center", justifyContent:"center", "& svg": { color: f.color } }}>{f.icon}</Box>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>{t(`home.features.${f.key}.label`)}</Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.75}>{t(`home.features.${f.key}.desc`)}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      {!authed && (
        <Box sx={{ borderRadius:"20px", p:{xs:5,md:8}, textAlign:"center", background:`linear-gradient(135deg, ${C.indigo[50]} 0%, ${C.teal[50]} 100%)`, border:`1px solid ${C.slate[200]}` }}>
          <Typography variant="h3" gutterBottom>{t("home.ctaTitle")}</Typography>
          <Typography color="text.secondary" sx={{ mb:4, fontSize:"1.05rem" }}>{t("home.ctaDesc")}</Typography>
          <Button size="large" variant="contained" component={Link} to="/register" endIcon={<ArrowForwardIcon />} sx={{ px:4, py:1.4 }}>
            {t("home.ctaButton")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
